import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  fromEvent,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  delay, delayWhen,
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap, take, timeout,
} from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConnectionStatusService } from '../connection-status.service';
import { MessageMap, Inbound } from '../../interfaces/websocket';
import { AuthService } from '../auth/auth.service';
import { RefreshResult } from '../../interfaces/user';

type Message<K extends keyof MessageMap> =
  K extends 'echoToWidget'
    ? { type: K } & MessageMap[K]
    : { type: K; data: MessageMap[K] };

type Outbound = Message<keyof MessageMap>;

@UntilDestroy({ checkProperties: true })
@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  public inboundMessage$ = new Subject<Inbound>();
  private outboundMessage$ = new Subject<Outbound>();

  private open$ = new Subject<any>();
  private closing$ = new Subject<any>();
  private close$ = new Subject<any>();

  private wsSub: Subscription | null = null;
  private timeoutSub: Subscription | null = null;
  private heartbeatSub: Subscription | null = null;
  private outboundMessageSub: Subscription | null = null;
  private updateMenuSub: Subscription | null = null;

  private readonly wsSubject$ = webSocket({
    url: `${environment.socketApi}/ws/main`,
    openObserver: this.open$,
    closingObserver: this.closing$,
    closeObserver: this.close$,
  });

  public connected$ = new BehaviorSubject<boolean>(false);
  public authorized$ = new BehaviorSubject<boolean>(false);

  constructor(
    private connectionStatus: ConnectionStatusService,
    private auth: AuthService,
  ) {
    combineLatest([this.connectionStatus.online$, this.auth.user$]).pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(([status, user]) => {

      if (status && user) {
        console.log('Connection status: online');
        this.subscribeWebSocket();
      } else {
        console.warn('Connection status: offline');
        this.unsubscribeWebSocket();
      }
    });

    this.connected$.pipe(
      filter((yes) => yes),
      filter(() => this.auth.hasTokens()),
      untilDestroyed(this)
    ).subscribe(() => {
      const token = this.auth.getTokens()?.token;

      if (token) {
        console.log('Auth: Sending an Authorization Token');
        this.authorize(token);
      }
      });

    fromEvent(document, 'visibilitychange').pipe(
      map(() => document.visibilityState === 'visible'),
      distinctUntilChanged(),
      filter((visible) => visible),
      filter(() => this.connectionStatus.online$.getValue()),
      filter(() => !this.connected$.getValue()),
      untilDestroyed(this)
    ).subscribe(() => {
      this.subscribeWebSocket();
    });

    this.open$.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      console.log('WebSocket: Open');
      this.connected$.next(true);
    });

    this.closing$.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      console.log('WebSocket: Closing');
    });

    this.close$.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      console.log('WebSocket: Close');
      this.connected$.next(false);
      this.authorized$.next(false);
    });
  }

  private unsubscribeWebSocket(): void {
    if (this.wsSub) {
      console.log(`WebSocket: Unsubscribe`);
    }

    this.wsSub?.unsubscribe();
    this.wsSub = null;
    this.timeoutSub?.unsubscribe();
    this.timeoutSub = null;
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = null;
    this.outboundMessageSub?.unsubscribe();
    this.outboundMessageSub = null;
    this.updateMenuSub?.unsubscribe();
    this.updateMenuSub = null;
  }

  private subscribeWebSocket(): void {
    this.unsubscribeWebSocket();

    console.log(`WebSocket: Start`);

    const wsMessages$ = this.wsSubject$.pipe(
      filter((response: any) => !!(response?.type)),
      share(),
    );

    this.timeoutSub = merge(of('init'), wsMessages$).pipe(
      switchMap(() => of('Restart with timeout').pipe(
        delay(10000)
      )),
      catchError(() => of('Restart with error').pipe(
        delay(3000)
      )),
      untilDestroyed(this),
    ).subscribe((msg) => {
      console.log(`WebSocket: ${msg}`);

      this.subscribeWebSocket();
    });

    this.heartbeatSub = timer(1000, 5000).pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      console.log('WebSocket: Heartbeat send');
      this.wsSubject$.next({ type: 'heartbeat' });
    });

    this.wsSub = wsMessages$.pipe(
      untilDestroyed(this),
    ).subscribe(
      (response: Inbound) => {
        if (response.type === 'loginSuccessful') {
          this.authorized$.next(true);
        }

        this.inboundMessage$.next(response);
      },
      (error) => console.error('WebSocket:', error),
      () => console.log('WebSocket: Complete'),
    );

    this.outboundMessageSub = this.outboundMessage$.pipe(
      delayWhen(() => this.authorized$.pipe(
        filter((status) => status)
      ))
    ).subscribe((message) => {
      this.wsSubject$.next(message);
    });
  }

  sendMessage<K extends keyof MessageMap>(
    type: K,
    data: MessageMap[K],
    force: boolean = false
  ): void {
    const message = (type === 'echoToWidget'
        ? { type, ...(data as MessageMap['echoToWidget']) }
        : { type, data }
    ) as Message<K>;

    if (force) {
      this.wsSubject$.next(message);
    } else {
      this.outboundMessage$.next(message);
    }
  }

  authorize(JWT: string): void {
    if (!this.connected$.getValue()) {
      this.subscribeWebSocket();
    }

    this.sendMessage('loginDashboard', { JWT }, true);
  }

  subscribeToRestaurant(id: number): void {
    this.sendMessage('channel.subscribe', { channel: `restaurant:${id}` });
  }

  subscribeToChain(id: number): void {
    this.sendMessage('channel.subscribe', { channel: `chain:${id}` });
  }

  unsubscribeFromRestaurant(id: number): void {
    this.sendMessage('channel.unsubscribe', { channel: `restaurant:${id}` });
  }

  unsubscribeFromChain(id: number): void {
    this.sendMessage('channel.unsubscribe', { channel: `chain:${id}` });
  }

  subscribe(next: (message: any) => void, error?: (error: any) => void, complete?: () => void): void {
    this.inboundMessage$.pipe(
      untilDestroyed(this),
    ).subscribe(next, error, complete);
  }

  close(): void {
    this.unsubscribeWebSocket();
  }

  public refreshTokensViaWS(refreshToken: string): Observable<RefreshResult> {
    return defer(() => {
      this.sendMessage('refreshDashboardTokens', { refreshToken });

      return this.inboundMessage$.pipe(
        filter((msg) => msg.type === 'authTokens' || msg.type === 'loginError'),
        take(1),
        timeout(10000),
        map((msg) =>
          msg.type === 'authTokens'
            ? ({
              accessToken: msg.data.accessToken,
              refreshToken: msg.data.refreshToken,
            } as RefreshResult)
            : ({
              error: msg.data ?? 'Token refresh failed',
            } as RefreshResult)
        ),
        catchError((err) => {
          console.error('Token refresh via WS failed', err);
          return of({
            error: err.message ?? 'Unknown WS error',
            transient: true,
          } as RefreshResult);
        })
      );
    });
  }

}
